import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Clock, 
  Users, 
  Award, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Copy,
  Calendar,
  BookOpen,
  School,
  GraduationCap,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import testesService from '../services/testesService';
import questoesService from '../services/questoesService';
import participantesService from '../services/participantesService';
import { getCurrentUserProfile } from '../services/usuariosService';
import Questoes from './Questoes';
import Participantes from './Participantes';
import '../styles/testes.css';

const Testes = () => {
  const [testes, setTestes] = useState([]);
  const [filteredTestes, setFilteredTestes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeste, setEditingTeste] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para questões
  const [showQuestoes, setShowQuestoes] = useState(false);
  const [selectedTeste, setSelectedTeste] = useState(null);
  const [questoesCount, setQuestoesCount] = useState({});
  const [viewMode, setViewMode] = useState('testes'); // 'testes', 'questoes' ou 'participantes'
  
  // Estados para participantes
  const [participantesCount, setParticipantesCount] = useState({});

  // Estados para dropdowns
  const [materias, setMaterias] = useState([]);
  const [anosEscolares, setAnosEscolares] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  // Estados para filtros
  const [filters, setFilters] = useState({
    search: '',
    materia_id: '',
    ano_escolar_id: '',
    escola_id: ''
  });

  // Estado do formulário
  const [formData, setFormData] = useState({
    nome: '',
    materia_id: '',
    ano_escolar_id: '',
    escola_id: '',
    descricao: '',
    ativo: true
  });

  const [formErrors, setFormErrors] = useState({});

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  // Aplicar filtros
  useEffect(() => {
    applyFilters();
  }, [testes, filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Carregar perfil do usuário logado
      const profileResult = await getCurrentUserProfile();
      if (profileResult.success) {
        setCurrentUserProfile(profileResult.data);
      }
      
      const [testesData, materiasData, anosData, escolasData] = await Promise.all([
        testesService.getAll(),
        testesService.getMaterias(),
        testesService.getAnosEscolares(),
        testesService.getEscolas()
      ]);

      setTestes(testesData);
      setMaterias(materiasData);
      setAnosEscolares(anosData);
      
      // Filtrar escolas baseado no perfil do usuário
      if (profileResult.success && profileResult.data?.school_adm && profileResult.data?.school_id) {
        const escolaDoUsuario = escolasData.filter(escola => escola.id === profileResult.data.school_id);
        setEscolas(escolaDoUsuario);
      } else {
        setEscolas(escolasData);
      }
      
      // Carregar contagem de questões e participantes para cada teste
      await loadQuestoesCount(testesData);
      await loadParticipantesCount(testesData);
    } catch (error) {
      setError('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestoesCount = async (testesData) => {
    try {
      const counts = {};
      await Promise.all(
        testesData.map(async (teste) => {
          const count = await questoesService.countByTesteId(teste.id);
          counts[teste.id] = count;
        })
      );
      setQuestoesCount(counts);
    } catch (error) {
      console.error('Erro ao carregar contagem de questões:', error);
    }
  };

  const loadParticipantesCount = async (testesData) => {
    try {
      const counts = {};
      await Promise.all(
        testesData.map(async (teste) => {
          const count = await participantesService.countByTesteId(teste.id);
          counts[teste.id] = count;
        })
      );
      setParticipantesCount(counts);
    } catch (error) {
      console.error('Erro ao carregar contagem de participantes:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...testes];

    // Se for school admin, filtrar apenas testes da escola do usuário
    if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
      filtered = filtered.filter(teste => teste.escola_id === currentUserProfile.school_id);
    }

    // Filtro por busca
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(teste => 
        teste.nome.toLowerCase().includes(searchLower) ||
        (teste.descricao && teste.descricao.toLowerCase().includes(searchLower))
      );
    }

    // Filtros por dropdowns
    if (filters.materia_id) {
      filtered = filtered.filter(teste => teste.materia_id === filters.materia_id);
    }

    if (filters.ano_escolar_id) {
      filtered = filtered.filter(teste => teste.ano_escolar_id === filters.ano_escolar_id);
    }

    if (filters.escola_id) {
      filtered = filtered.filter(teste => teste.escola_id === filters.escola_id);
    }

    setFilteredTestes(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      materia_id: '',
      ano_escolar_id: '',
      escola_id: ''
    });
  };

  const openModal = (teste = null) => {
    if (teste) {
      setEditingTeste(teste);
      setFormData({
        nome: teste.nome || '',
        materia_id: teste.materia_id || '',
        ano_escolar_id: teste.ano_escolar_id || '',
        escola_id: teste.escola_id || '',
        descricao: teste.descricao || '',
        ativo: teste.ativo !== false
      });
    } else {
      setEditingTeste(null);
      setFormData({
        nome: '',
        materia_id: '',
        ano_escolar_id: '',
        escola_id: currentUserProfile?.school_adm && currentUserProfile?.school_id ? currentUserProfile.school_id : '',
        descricao: '',
        ativo: true
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTeste(null);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    }

    if (!formData.materia_id) {
      errors.materia_id = 'Matéria é obrigatória';
    }

    if (!formData.ano_escolar_id) {
      errors.ano_escolar_id = 'Ano escolar é obrigatório';
    }

    if (!formData.escola_id) {
      errors.escola_id = 'Escola é obrigatória';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpar erro do campo quando usuário começar a digitar
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const testeData = { ...formData };

      let savedTeste;
      if (editingTeste) {
        savedTeste = await testesService.update(editingTeste.id, testeData);
        setTestes(prev => prev.map(t => t.id === editingTeste.id ? savedTeste : t));
        setSuccess('Teste atualizado com sucesso!');
      } else {
        savedTeste = await testesService.create(testeData);
        setTestes(prev => [savedTeste, ...prev]);
        setSuccess('Teste criado com sucesso!');
      }

      closeModal();
    } catch (error) {
      setError('Erro ao salvar teste: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (teste) => {
    if (!window.confirm(`Tem certeza que deseja excluir o teste "${teste.nome}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await testesService.delete(teste.id);
      setTestes(prev => prev.filter(t => t.id !== teste.id));
      setSuccess('Teste excluído com sucesso!');
    } catch (error) {
      setError('Erro ao excluir teste: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (teste) => {
    try {
      const updatedTeste = await testesService.toggleActive(teste.id, !teste.ativo);
      setTestes(prev => prev.map(t => t.id === teste.id ? { ...t, ativo: updatedTeste.ativo } : t));
      setSuccess(`Teste ${updatedTeste.ativo ? 'ativado' : 'desativado'} com sucesso!`);
    } catch (error) {
      setError('Erro ao alterar status: ' + error.message);
    }
  };

  const handleDuplicate = async (teste) => {
    try {
      setLoading(true);
      const duplicatedTeste = await testesService.duplicate(teste.id);
      setTestes(prev => [duplicatedTeste, ...prev]);
      setSuccess('Teste duplicado com sucesso!');
      
      // Atualizar contagem de questões
      const count = await questoesService.countByTesteId(duplicatedTeste.id);
      setQuestoesCount(prev => ({ ...prev, [duplicatedTeste.id]: count }));
    } catch (error) {
      setError('Erro ao duplicar teste: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenQuestoes = (teste) => {
    setSelectedTeste(teste);
    setViewMode('questoes');
  };

  const handleCloseQuestoes = () => {
    setViewMode('testes');
    setSelectedTeste(null);
    // Recarregar contagem de questões
    if (selectedTeste) {
      loadQuestoesCount([selectedTeste]);
    }
  };

  const handleOpenParticipantes = (teste) => {
    setSelectedTeste(teste);
    setViewMode('participantes');
  };

  const handleCloseParticipantes = () => {
    setViewMode('testes');
    setSelectedTeste(null);
    // Recarregar contagem de participantes
    if (selectedTeste) {
      loadParticipantesCount([selectedTeste]);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'rascunho': return <Edit size={16} />;
      case 'agendado': return <Calendar size={16} />;
      case 'em_andamento': return <Play size={16} />;
      case 'finalizado': return <CheckCircle size={16} />;
      case 'cancelado': return <XCircle size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'rascunho': return 'status-draft';
      case 'agendado': return 'status-scheduled';
      case 'em_andamento': return 'status-progress';
      case 'finalizado': return 'status-completed';
      case 'cancelado': return 'status-cancelled';
      default: return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  // Calcular estatísticas
  const stats = {
    total: currentUserProfile?.school_adm && currentUserProfile?.school_id 
      ? testes.filter(t => t.escola_id === currentUserProfile.school_id).length
      : testes.length,
    ativos: currentUserProfile?.school_adm && currentUserProfile?.school_id 
      ? testes.filter(t => t.escola_id === currentUserProfile.school_id && t.ativo).length
      : testes.filter(t => t.ativo).length,
    inativos: currentUserProfile?.school_adm && currentUserProfile?.school_id 
      ? testes.filter(t => t.escola_id === currentUserProfile.school_id && !t.ativo).length
      : testes.filter(t => !t.ativo).length
  };

  if (loading && testes.length === 0) {
    return (
      <div className="testes-container">
        <div className="loading">Carregando testes...</div>
      </div>
    );
  }

  // Se estiver no modo questões, mostrar apenas o componente de questões
  if (viewMode === 'questoes' && selectedTeste) {
    return (
      <div className="testes-container">
        <Questoes 
          teste={selectedTeste} 
          onClose={handleCloseQuestoes} 
        />
      </div>
    );
  }

  // Se estiver no modo participantes, mostrar apenas o componente de participantes
  if (viewMode === 'participantes' && selectedTeste) {
    return (
      <div className="testes-container">
        <Participantes 
          teste={selectedTeste} 
          onClose={handleCloseParticipantes} 
        />
      </div>
    );
  }

  return (
    <div className="testes-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">Testes</h1>
            <p className="page-subtitle">Gerenciamento de avaliações e provas</p>
          </div>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <Plus size={20} />
            Novo Teste
          </button>
        </div>
      </div>

      {/* Mensagens */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess('')} className="alert-close">×</button>
        </div>
      )}

      {/* Estatísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <ClipboardList size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total de Testes</div>
        </div>

        <div className="stat-card success">
          <div className="stat-header">
            <div className="stat-icon success">
              <CheckCircle size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.ativos}</div>
          <div className="stat-label">Ativos</div>
        </div>

        <div className="stat-card warning">
          <div className="stat-header">
            <div className="stat-icon warning">
              <XCircle size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.inativos}</div>
          <div className="stat-label">Inativos</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por nome ou descrição..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <select
            value={filters.materia_id}
            onChange={(e) => handleFilterChange('materia_id', e.target.value)}
          >
            <option value="">Todas as Matérias</option>
            {materias.map(materia => (
              <option key={materia.id} value={materia.id}>{materia.nome}</option>
            ))}
          </select>

          <select
            value={filters.ano_escolar_id}
            onChange={(e) => handleFilterChange('ano_escolar_id', e.target.value)}
          >
            <option value="">Todos os Anos</option>
            {anosEscolares.map(ano => (
              <option key={ano.id} value={ano.id}>{ano.nome}</option>
            ))}
          </select>

          {!currentUserProfile?.school_adm ? (
            <select
              value={filters.escola_id}
              onChange={(e) => handleFilterChange('escola_id', e.target.value)}
            >
              <option value="">Todas as Escolas</option>
              {escolas.map(escola => (
                <option key={escola.id} value={escola.id}>{escola.nome}</option>
              ))}
            </select>
          ) : null}



          <button className="btn btn-secondary" onClick={clearFilters}>
            <Filter size={16} />
            Limpar
          </button>
        </div>
      </div>

      {/* Lista de Testes */}
      <div className="content-card">
        {filteredTestes.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={48} />
            <h3>Nenhum teste encontrado</h3>
            <p>
              {testes.length === 0 
                ? 'Comece criando seu primeiro teste.' 
                : 'Tente ajustar os filtros para encontrar o que procura.'
              }
            </p>
            {testes.length === 0 && (
              <button className="btn btn-primary" onClick={() => openModal()}>
                <Plus size={20} />
                Criar Primeiro Teste
              </button>
            )}
          </div>
        ) : (
          <div className="testes-grid">
            {filteredTestes.map(teste => (
              <div key={teste.id} className={`teste-card ${!teste.ativo ? 'inactive' : ''}`}>
                <div className="teste-header">
                  <div className="teste-title">
                    <h3>{teste.nome}</h3>
                  </div>
                  <div className="teste-actions">
                    <button
                      className="btn-icon"
                      onClick={() => openModal(teste)}
                      title="Editar"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDuplicate(teste)}
                      title="Duplicar"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDelete(teste)}
                      title="Excluir"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="teste-info">
                  <div className="info-row">
                    <BookOpen size={16} />
                    <span>{teste.materias?.nome || 'Matéria não definida'}</span>
                  </div>
                  <div className="info-row">
                    <GraduationCap size={16} />
                    <span>{teste.anos_escolares?.nome || 'Ano não definido'}</span>
                  </div>
                  <div className="info-row">
                    <School size={16} />
                    <span>{teste.escolas?.nome || 'Escola não definida'}</span>
                  </div>
                  <div className="info-row">
                    <HelpCircle size={16} />
                    <span>{questoesCount[teste.id] || 0} questões</span>
                  </div>
                  <div className="info-row">
                    <Users size={16} />
                    <span>{participantesCount[teste.id] || 0} participantes</span>
                  </div>
                </div>

                {teste.descricao && (
                  <div className="teste-description">
                    <p>{teste.descricao}</p>
                  </div>
                )}

                <div className="teste-footer">
                  <div className="footer-buttons">
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => handleOpenQuestoes(teste)}
                      title="Gerenciar questões"
                    >
                      <HelpCircle size={16} />
                      Questões ({questoesCount[teste.id] || 0})
                    </button>
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => handleOpenParticipantes(teste)}
                      title="Gerenciar participantes"
                    >
                      <Users size={16} />
                      Participantes ({participantesCount[teste.id] || 0})
                    </button>
                  </div>
                  
                  <div className="toggle-container">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={teste.ativo}
                        onChange={() => handleToggleActive(teste)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">
                      {teste.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTeste ? 'Editar Teste' : 'Novo Teste'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              <form className="teste-form">
                {/* Informações do Teste */}
                <div className="form-section">
                  <h3>Informações do Teste</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Nome do Teste *</label>
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        className={formErrors.nome ? 'error' : ''}
                        placeholder="Ex: Prova de Matemática - 1º Bimestre"
                      />
                      {formErrors.nome && <span className="error-message">{formErrors.nome}</span>}
                    </div>

                    <div className="form-group">
                      <label>Matéria *</label>
                      <select
                        name="materia_id"
                        value={formData.materia_id}
                        onChange={handleInputChange}
                        className={formErrors.materia_id ? 'error' : ''}
                      >
                        <option value="">Selecione uma matéria</option>
                        {materias.map(materia => (
                          <option key={materia.id} value={materia.id}>{materia.nome}</option>
                        ))}
                      </select>
                      {formErrors.materia_id && <span className="error-message">{formErrors.materia_id}</span>}
                    </div>

                    <div className="form-group">
                      <label>Ano Escolar *</label>
                      <select
                        name="ano_escolar_id"
                        value={formData.ano_escolar_id}
                        onChange={handleInputChange}
                        className={formErrors.ano_escolar_id ? 'error' : ''}
                      >
                        <option value="">Selecione um ano</option>
                        {anosEscolares.map(ano => (
                          <option key={ano.id} value={ano.id}>{ano.nome}</option>
                        ))}
                      </select>
                      {formErrors.ano_escolar_id && <span className="error-message">{formErrors.ano_escolar_id}</span>}
                    </div>

                    <div className="form-group">
                      <label>Escola *</label>
                      {currentUserProfile?.school_adm && escolas.length > 0 ? (
                        <input
                          type="text"
                          value={escolas[0].nome}
                          disabled
                          className="disabled-input"
                        />
                      ) : (
                        <select
                          name="escola_id"
                          value={formData.escola_id}
                          onChange={handleInputChange}
                          className={formErrors.escola_id ? 'error' : ''}
                        >
                          <option value="">Selecione uma escola</option>
                          {escolas.map(escola => (
                            <option key={escola.id} value={escola.id}>{escola.nome}</option>
                          ))}
                        </select>
                      )}
                      {formErrors.escola_id && <span className="error-message">{formErrors.escola_id}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Descrição</label>
                    <textarea
                      name="descricao"
                      value={formData.descricao}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Descrição opcional do teste..."
                    />
                  </div>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="ativo"
                        checked={formData.ativo}
                        onChange={handleInputChange}
                      />
                      <span>Teste ativo</span>
                    </label>
                  </div>
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Salvando...' : (editingTeste ? 'Atualizar' : 'Criar')}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Testes;
